import React, { useState } from 'react'
import { Button, OverlayTrigger, Table, Tooltip, Spinner, Modal, InputGroup, FormControl, Card } from 'react-bootstrap'
import moment from 'moment'
import Swal from 'sweetalert2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarCheck, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'

import AgendamentoService from '../../services/agendamentos.service'
import AtendimentoService from '../../services/atendimento.service'
import { rotaAvaliacaoOrtodontia } from '../../constants/agendamento.constants'

export default function AvaliacoesPendentes({
  avaliacoesPendentesList,
  getAvaliacoesPendentesList,
  concluirAtendimento,
  setIsLoading,
  history,
  paciente,
}) {
  const handleConcluir = async (avaliacaoItem) => {
    setIsLoading(true)
    try {
      const { data } = await AgendamentoService.getById(avaliacaoItem.agendamentoId)
      concluirAtendimento(data)
      if (data.avaliacaoOrtodontia) {
        history.push(rotaAvaliacaoOrtodontia(data.avaliacaoOrtodontia))
      } else {
        history.push('/atendimento-financeiro', { pedirTipoContrato: true })
      }
    } catch (e) {
      console.error(e)
    }
    setIsLoading(false)
  }

  const [copiandoLinkId, setCopiandoLinkId] = useState(null)
  const [modalLinks, setModalLinks] = useState({ show: false, links: [], titulo: '' })

  const handleCopiarLink = async (avaliacaoItem) => {
    if (!avaliacaoItem.aguardandoAssinatura || !avaliacaoItem.avaliacaoId) return
    setCopiandoLinkId(avaliacaoItem.avaliacaoId)
    const nomeClasse = avaliacaoItem.isOrtodontia ? 'AvaliacaoOrtodontia' : 'Avaliacao'
    try {
      const { data } = await AtendimentoService.getContratosAssinatura(nomeClasse, avaliacaoItem.avaliacaoId)
      const primeiro = Array.isArray(data) && data.length > 0 ? data[0] : null
      const links = primeiro?.linksAssinatura
      const linkUnico = primeiro?.linkAssinatura
      if (links && links.length > 0) {
        setModalLinks({
          show: true,
          links,
          titulo: `Links de assinatura - ${avaliacaoItem.nomeDependente || avaliacaoItem.nomePaciente || 'Avaliação'}`,
        })
      } else if (linkUnico) {
        setModalLinks({
          show: true,
          links: [{ qualificacao: 'Paciente', link: linkUnico }],
          titulo: `Link de assinatura - ${avaliacaoItem.nomeDependente || avaliacaoItem.nomePaciente || 'Avaliação'}`,
        })
      } else {
        toast.info('Nenhum link disponível para esta avaliação.')
      }
    } catch (e) {
      toast.error('Erro ao obter link.')
    }
    setCopiandoLinkId(null)
  }

  const copiarLinkModal = (url) => {
    if (!url) return
    navigator.clipboard.writeText(url).then(() => toast.success('Link copiado.')).catch(() => toast.error('Não foi possível copiar.'))
  }

  const handleCancelar = async (avaliacao) => {
    const { value } = await Swal.fire({
      title: 'Tem certeza que deseja cancelar esta avaliação?',
      type: 'warning',
      showCancelButton: true,
      cancelButtonColor: '#dbdbdb',
      cancelButtonText: 'Não Cancelar',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Cancelar',
    })
    if (value) {
      setIsLoading(true)
      try {
        await AtendimentoService.cancelarAvaliacao(avaliacao.agendamentoId)
        await getAvaliacoesPendentesList()
      } catch (e) {
        console.error(e)
      }
      setIsLoading(false)
    }
  }

  const renderAcoes = (avaliacao) => (
    <div className='d-flex justify-content-center justify-content-md-around flex-wrap align-items-center' style={{ gap: '8px' }}>
      {avaliacao.aguardandoAssinatura && (
        <OverlayTrigger trigger='hover' placement='top' overlay={<Tooltip>Copiar link de assinatura</Tooltip>}>
          <Button
            onClick={() => handleCopiarLink(avaliacao)}
            variant='outline-primary'
            size='sm'
            disabled={copiandoLinkId === avaliacao.avaliacaoId}
          >
            {copiandoLinkId === avaliacao.avaliacaoId ? <Spinner animation='border' size='sm' /> : <FontAwesomeIcon icon={faCopy} />}
          </Button>
        </OverlayTrigger>
      )}
      {!avaliacao.aguardandoAssinatura && (
        <OverlayTrigger trigger='hover' placement='top' overlay={<Tooltip>Concluir Atendimento</Tooltip>}>
          <Button onClick={() => handleConcluir(avaliacao)} variant='primary' size='sm'>
            <FontAwesomeIcon icon={faCalendarCheck} />
          </Button>
        </OverlayTrigger>
      )}
      <OverlayTrigger trigger='hover' placement='top' overlay={<Tooltip>Cancelar Atendimento</Tooltip>}>
        <Button onClick={() => handleCancelar(avaliacao)} variant='danger' size='sm'>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </OverlayTrigger>
    </div>
  )

  return (
    <>
      <div className='d-none d-md-block'>
        <div
          className='table-responsive plano-tratamento-detalhe-scroll'
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <Table className='mt-3 mb-0' bordered hover style={{ minWidth: '720px' }}>
            <thead>
              <tr className='text-center'>
                <th>Paciente</th>
                <th>Responsável</th>
                <th>Descrição</th>
                <th width='120px'>Data</th>
                <th width='160px'>Status</th>
                <th width='140px'>Ações</th>
              </tr>
            </thead>
            <tbody>
              {avaliacoesPendentesList.map((avaliacao, index) => {
                const { nomeDependente, nomePaciente, nomeResponsavel, descricao, dataCriacao, aguardandoAssinatura } = avaliacao
                return (
                  <tr key={index} className='text-center'>
                    <td>{nomeDependente || nomePaciente}</td>
                    <td>{nomeResponsavel}</td>
                    <td>{descricao || '-'}</td>
                    <td>{moment(dataCriacao).format('DD/MM/YYYY')}</td>
                    <td>
                      {aguardandoAssinatura ? (
                        <span className='badge badge-info'>Pendente de assinatura</span>
                      ) : (
                        <span className='badge badge-secondary'>Pendente</span>
                      )}
                    </td>
                    <td>{renderAcoes(avaliacao)}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </div>

      <div className='d-md-none'>
        {avaliacoesPendentesList.map((avaliacao, index) => {
          const { nomeDependente, nomePaciente, nomeResponsavel, descricao, dataCriacao, aguardandoAssinatura } = avaliacao
          return (
            <Card key={index} className='mb-3 shadow-sm border plano-tratamento-card-mobile'>
              <Card.Body className='p-3'>
                <div className='small'>
                  <div className='d-flex justify-content-between py-2 border-bottom'>
                    <span className='text-muted'>Paciente</span>
                    <span className='text-dark text-right font-weight-bold'>{nomeDependente || nomePaciente}</span>
                  </div>
                  <div className='d-flex justify-content-between py-2 border-bottom'>
                    <span className='text-muted'>Responsável</span>
                    <span className='text-dark text-right'>{nomeResponsavel}</span>
                  </div>
                  <div className='d-flex justify-content-between py-2 border-bottom'>
                    <span className='text-muted'>Data</span>
                    <span className='text-dark'>{moment(dataCriacao).format('DD/MM/YYYY')}</span>
                  </div>
                  <div className='d-flex justify-content-between py-2 border-bottom'>
                    <span className='text-muted'>Status</span>
                    <span>
                      {aguardandoAssinatura ? (
                        <span className='badge badge-info'>Pendente de assinatura</span>
                      ) : (
                        <span className='badge badge-secondary'>Pendente</span>
                      )}
                    </span>
                  </div>
                  <div className='pt-2'>
                    <span className='text-muted d-block mb-1'>Descrição</span>
                    <span className='text-dark'>{descricao || '-'}</span>
                  </div>
                </div>
                <div className='mt-3 pt-2 border-top'>{renderAcoes(avaliacao)}</div>
              </Card.Body>
            </Card>
          )
        })}
      </div>

      <Modal show={modalLinks.show} onHide={() => setModalLinks((p) => ({ ...p, show: false }))} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>Links de assinatura</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-muted small mb-3'>{modalLinks.titulo}</p>
          {modalLinks.links.map((item, idx) => (
            <div key={idx} className='mb-3'>
              <label className='d-block text-left small font-weight-bold text-secondary mb-1'>{item.qualificacao}:</label>
              <InputGroup>
                <FormControl readOnly value={item.link || ''} className='small' />
                <InputGroup.Append>
                  <Button variant='outline-secondary' onClick={() => copiarLinkModal(item.link)} title={`Copiar link - ${item.qualificacao}`}>
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </InputGroup.Append>
              </InputGroup>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </>
  )
}
